import { useEffect, useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox, cn } from "@/design-system/components/react";
import { errorStatus, notifyErrorText } from "./api";
import { alertSentText, clockTime, ownerName } from "./format";
import { useNotifyOutlet } from "./NotifyLayout";
import { useCaseList, useRefetchMeOnForbidden } from "./queries";
import { canReadCases, pickSite, siteLabel } from "./site";
import { badgeState, rowRule, StateBadge, TestTag } from "./StateBadge";
import { CASE_STATES, type CaseState, type NotifyCase } from "./types";

type Filter = { all: boolean } & Record<CaseState, boolean>;

/** What needs action: open and alerted cases. */
const DEFAULT_FILTER: Filter = { all: false, open: true, alerted: true, acknowledged: false };

const STATE_LABEL: Record<CaseState, string> = {
  open: "Open",
  alerted: "Alerted",
  acknowledged: "Acknowledged",
};

export const notifyCaseUrl = (row: Pick<NotifyCase, "site_id" | "study_uid">) =>
  `/notify/cases/${encodeURIComponent(row.site_id)}/${encodeURIComponent(row.study_uid)}`;

/** One filter box as a chip: ticked = selected cue (signal-tint), locked = dimmed. */
function FilterBox({
  label,
  checked,
  disabled,
  onChange,
  strong,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  strong?: boolean;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[1px] border py-[3px] pl-1.5 pr-2 text-xs",
        checked
          ? "border-transparent bg-signal-tint text-signal-text-tint"
          : "border-border text-fg-muted",
        disabled && "cursor-not-allowed opacity-50",
        strong && "font-semibold",
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
        className="size-3.5 border"
      />
      {label}
    </label>
  );
}

/** All / Open / Alerted / Acknowledged. All ticks and locks the others; untick
 * it to pick states one by one. */
function CaseFilter({ filter, onChange }: { filter: Filter; onChange: (filter: Filter) => void }) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1 border-0 p-0">
      <legend className="sr-only">Show cases</legend>
      <FilterBox
        label="All"
        strong
        checked={filter.all}
        onChange={(all) => onChange({ ...filter, all })}
      />
      <span aria-hidden className="mx-0.5 my-[3px] w-px bg-border-strong" />
      {CASE_STATES.map((state) => (
        <FilterBox
          key={state}
          label={STATE_LABEL[state]}
          checked={filter.all || filter[state]}
          disabled={filter.all}
          onChange={(checked) => onChange({ ...filter, [state]: checked })}
        />
      ))}
    </fieldset>
  );
}

/** Phone: stacked rows, the whole row a link. */
function CaseRows({ cases, me }: { cases: NotifyCase[]; me: string | null }) {
  return (
    <ul className="-mx-4 border-t border-border sm:hidden">
      {cases.map((row) => {
        const owner = ownerName(row, me);
        return (
          <li
            key={row.study_uid}
            data-testid="notify-case-row"
            className={cn(
              "border-b border-l-2 border-b-border",
              rowRule(row.state),
              row.state === "acknowledged" && "opacity-60",
            )}
          >
            <Link to={notifyCaseUrl(row)} className="grid gap-1 py-3 pl-3.5 pr-4">
              <span className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 truncate font-mono text-xs">{row.study_uid}</span>
                {row.is_test && <TestTag />}
                <span className="ml-auto">
                  <StateBadge state={badgeState(row)} />
                </span>
              </span>
              <span className="flex flex-wrap gap-3 font-mono text-[11px] tabular-nums text-fg-muted">
                <span>flagged {clockTime(row.flagged_at)}</span>
                {row.acknowledged_at ? (
                  <span>ack {clockTime(row.acknowledged_at)}</span>
                ) : (
                  <span>alert {alertSentText(row)}</span>
                )}
                <span>{owner ? `owner ${owner}` : "no owner"}</span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

const TH =
  "border-b border-border px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-fg-dim";
const TD =
  "border-b border-border px-3 py-2.5 font-mono text-[11px] whitespace-nowrap text-fg-muted";

/** Wider screens: a table, each row opening its case. */
function CaseTable({ cases, me }: { cases: NotifyCase[]; me: string | null }) {
  const navigate = useNavigate();
  return (
    <table className="hidden w-full border-collapse text-left text-xs tabular-nums sm:table">
      <thead>
        <tr>
          <th className={TH}>Study</th>
          <th className={TH}>State</th>
          <th className={TH}>Flagged</th>
          <th className={TH}>Alert sent</th>
          <th className={TH}>Owner</th>
          <th className={cn(TH, "text-right")}>Acknowledged</th>
        </tr>
      </thead>
      <tbody>
        {cases.map((row) => {
          const owner = ownerName(row, me);
          return (
            <tr
              key={row.study_uid}
              data-testid="notify-case-row"
              onClick={() => navigate(notifyCaseUrl(row))}
              className={cn(
                "cursor-pointer border-l-2 hover:bg-surface-2",
                rowRule(row.state),
                row.state === "acknowledged" && "opacity-60",
              )}
            >
              <td className="border-b border-border px-3 py-2.5 font-mono text-xs text-fg">
                <span className="flex items-center gap-2">
                  <Link to={notifyCaseUrl(row)} onClick={(event) => event.stopPropagation()}>
                    {row.study_uid}
                  </Link>
                  {row.is_test && <TestTag />}
                </span>
              </td>
              <td className="border-b border-border px-3 py-2.5">
                <StateBadge state={badgeState(row)} />
              </td>
              <td className={TD}>{clockTime(row.flagged_at)}</td>
              <td className={TD}>{alertSentText(row)}</td>
              <td className={cn(TD, !owner && "text-fg-dim")}>{owner ?? "none"}</td>
              <td className={cn(TD, "text-right")}>
                {row.acknowledged_at ? clockTime(row.acknowledged_at) : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * The site's cases for radiologists and admins, oldest flag first, filtered by
 * state (default: Open + Alerted). A tap opens the case. A case acknowledged
 * here leaves the Open/Alerted view at once, whatever the list read says.
 * Other roles have no list: their alert opens its case page directly.
 */
export default function NotifyCaseList() {
  const { me } = useNotifyOutlet();
  const site = pickSite(me)!;
  const [filter, setFilter] = useState<Filter>(DEFAULT_FILTER);
  const states = filter.all ? CASE_STATES : CASE_STATES.filter((state) => filter[state]);
  const myId = site.person_id;
  const canRead = canReadCases(site);
  // Other roles get 403 on the list, so they never ask for it.
  const { cases, isLoading, error } = useCaseList(site.site_id, canRead ? states : []);
  // A 403 here may mean the caller's role changed: /me decides this page.
  const refetchMeOnForbidden = useRefetchMeOnForbidden();
  useEffect(() => {
    if (error) refetchMeOnForbidden(error);
  }, [error, refetchMeOnForbidden]);

  if (!canRead) {
    return (
      <p className="p-6 text-sm text-fg-muted">
        Your Notify alerts open here when you tap them. There is no case list for your role.
      </p>
    );
  }
  return (
    <main className="flex flex-1 flex-col gap-5 px-4 pb-6 pt-5 sm:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
        <h1 className="text-sm font-medium">{siteLabel(site)} cases</h1>
        {!isLoading && !error && (
          <span className="font-mono text-[11px] tabular-nums text-fg-dim">
            {cases.length} shown
          </span>
        )}
        <div className="w-full sm:ml-auto sm:w-auto">
          <CaseFilter filter={filter} onChange={setFilter} />
        </div>
      </div>
      {error ? (
        <div role="alert" className="grid gap-1 text-sm text-fg-muted">
          <p className="font-semibold text-fg">Could not load cases: {notifyErrorText(error)}</p>
          {errorStatus(error) === 403 && <p>Ask the Notify admin to add you.</p>}
        </div>
      ) : isLoading ? (
        <p className="text-sm text-fg-dim">Loading cases…</p>
      ) : states.length === 0 ? (
        <p className="text-[13px] text-fg-dim">Tick a state to show cases.</p>
      ) : cases.length === 0 ? (
        <p className="text-[13px] text-fg-dim">No cases</p>
      ) : (
        <>
          <CaseRows cases={cases} me={myId} />
          <CaseTable cases={cases} me={myId} />
        </>
      )}
    </main>
  );
}
